import { useSyncExternalStore } from "react";

import { MS_IN_SECOND } from "../constants";
import { formatTime } from "../formatters";

/**
 * Shared 1 Hz ticker implemented as an external store.
 *
 * Every mounted stopwatch subscribes to the SAME module-level timer instead of
 * creating its own interval, so N stopwatches cost one `setInterval`, not N. The
 * `subscribe`/`getSnapshot` pair is consumed by React's `useSyncExternalStore`,
 * which requires `subscribe` to be a stable reference (defined once, here) and
 * `getSnapshot` to return the same value until the store actually changes.
 */
type TickListener = () => void;
const listeners = new Set<TickListener>();
let intervalId: ReturnType<typeof setInterval> | null = null;

/**
 * Cached snapshot of the ticker: the current second (epoch ms). The interval owns
 * this value and only advances it when the second actually changes, so `getSnapshot`
 * returns the same primitive between ticks. `null` while no interval is running.
 */
let currentSnapshot: number | null = null;

/** Current time floored to the second, so the snapshot only changes once per second. */
function getCurrentTime(): number {
    return Math.floor(Date.now() / 1000) * 1000;
}

/**
 * Subscribe a listener to the shared ticker. Starts the single interval on the
 * first subscriber and stops it once the last subscriber leaves. Stable identity
 * (module scope) so React never re-subscribes on re-render.
 */
function subscribe(listener: TickListener): () => void {
    listeners.add(listener);

    if (intervalId === null) {
        // Seed the cache when the ticker starts so subscribers read a fresh value.
        currentSnapshot = getCurrentTime();
        intervalId = setInterval(() => {
            const nextSnapshot = getCurrentTime();
            // Only advance the cached snapshot (and notify) when the second actually
            // changes, keeping getSnapshot's return value stable between real ticks.
            if (nextSnapshot !== currentSnapshot) {
                currentSnapshot = nextSnapshot;
                listeners.forEach((tickListener) => {
                    tickListener();
                });
            }
        }, MS_IN_SECOND);
    }

    return () => {
        listeners.delete(listener);

        if (listeners.size === 0 && intervalId !== null) {
            clearInterval(intervalId);
            intervalId = null;
            // Drop the cache so the next start reads fresh instead of a stale second.
            currentSnapshot = null;
        }
    };
}

/**
 * Snapshot of the ticker for `useSyncExternalStore`. While subscribed this returns the
 * interval-maintained cached value, so repeated calls (React's render-phase read and its
 * post-commit tearing check) return the same primitive until the interval notifies of a
 * new second. Before the first subscriber exists there is nothing to tear against, so it
 * reads live to keep the very first paint fresh; `subscribe` then seeds the cache.
 */
function getSnapshot(): number {
    return currentSnapshot ?? getCurrentTime();
}

/**
 * Whole seconds elapsed since `startTime` (epoch ms) with safety bounds to prevent negative time.
 * Use this for guards (e.g. hide UI after N seconds) so logic cannot drift from the hook.
 * @param startTime epoch time in ms that the timer should start from
 * @param now epoch time in ms to measure against; defaults to the current second. `useStopwatch`
 * passes the shared ticker's snapshot here; standalone guard callers can omit it.
 */
export function getStopwatchElapsedSeconds(startTime?: number | null, now: number = getCurrentTime()): number {
    if (!startTime) return 0;

    const elapsedTime = Math.floor((now - startTime) / 1000);
    return Math.max(elapsedTime, 0); // Ensure we never show negative time
}

/**
 * A hook that returns the time elapsed since the startTime. If no start time is provided the timer will start from 0.
 * Hiding a stopwatch with no start time should be intentionally handled by the consuming component.
 * All instances share a single 1 Hz ticker (via `useSyncExternalStore`) to stay synchronized cheaply.
 * @param startTime epoch time in ms that the timer should start from
 * @param appendUnit Whether to append the unit to the formatted string (ie. sec or min)
 * @param format The format to use. see: https://day.js.org/docs/en/display/format
 * @returns A formatted string representing the elapsed time since startTime
 */
export function useStopwatch(startTime?: number | null, appendUnit = false, format?: string): string {
    // Third arg (server snapshot) mirrors the client snapshot: harmless for this CSR app,
    // and avoids throwing if these components are ever rendered under a server/test renderer.
    const now = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const secondsElapsed = getStopwatchElapsedSeconds(startTime, now);

    return formatTime(secondsElapsed, appendUnit, format);
}
